import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { ProviderStockLocations, useStockLocations } from "../../api/stocklocations";
import SearchField from "../../interface/shared/searchfield";
import { ProviderAuditLogEntries, useAuditLogEntries } from "../../api/audit";
import { useNavigate } from "react-router";


export default function Audit() {
    return (
        <DashboardLayout>
            <ProviderAuditLogEntries>
                <AuditContent />
            </ProviderAuditLogEntries>
        </DashboardLayout>
    );
}

function AuditContent() {
    const [searchTerm, setSearchTerm] = React.useState("");
    const { auditLogEntries, errors, loading, fetchAuditLogEntries } = useAuditLogEntries();
    const navigate = useNavigate();

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                        Audit Log
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Audit log entries for the warehouse.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SearchField
                        placeholder="Search audit log entries by user or event"
                        value={searchTerm}
                        onChange={setSearchTerm}
                    />
                </Stack>
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchAuditLogEntries}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Event</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                    </TableRow>
                                ))
                            ) : auditLogEntries.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                            No audit log entries found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                auditLogEntries.map((auditLogEntry) => (
                                    <TableRow key={`${auditLogEntry.id}`}>
                                        <TableCell>{auditLogEntry.created_at.toLocaleString()}</TableCell>
                                        <TableCell>{auditLogEntry.user.name}</TableCell>
                                        <TableCell sx={{ textTransform: "capitalize" }}>{auditLogEntry.event}</TableCell>
                                        <TableCell>{auditLogEntry.display_type}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                onClick={() => navigate(`/audit/${auditLogEntry.id}`)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
}